import Link from 'next/link'
import React from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Box, styled, Typography } from '@mui/material'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'

import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary, StyledAngleIcon } from './StyledAccordion'

export type FooterLinkProps = {
  name: string
  href?: string
  header?: boolean
  target?: '_self' | '_blank'
}

export const FooterLink: React.FC<FooterLinkProps> = ({ header, name, href, target = '_self' }) => {
  const linkContent = (
    <StyledLinkTypography variant="body1">
      {name}
    </StyledLinkTypography>
  )

  if (header) {
    return (
      <>
        <StyledListItem>
          <StyledHeaderTypography variant="body1">
            {name}
          </StyledHeaderTypography>
        </StyledListItem>

        <StyledAccordionSummary
          expandIcon={<StyledAngleIcon iconCode="anglesmalldown" />}
          aria-controls="panel2a-content"
          id="panel2a-header"
        >
          <StyledHeaderTypography>
            {name}
          </StyledHeaderTypography>
        </StyledAccordionSummary>
      </>
    )
  }

  if (!href) return null

  return (
    <>
      <StyledListItem>
        {href.startsWith('http') ? (
          <StyledExternalLink
            href={href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          >
            {linkContent}
          </StyledExternalLink>
        ) : (
          <StyledInternalLink href={href} target={target} passHref>
            {linkContent}
          </StyledInternalLink>
        )}
      </StyledListItem>
      <StyledAccordionDetails>
        {href.startsWith('http') ? (
          <StyledExternalLink
            href={href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          >
            {linkContent}
          </StyledExternalLink>
        ) : (
          <StyledInternalLink href={href} target={target} passHref>
            {linkContent}
          </StyledInternalLink>
        )}
      </StyledAccordionDetails>
    </>
  )
}

export type FooterLinkListProps = {
  children: NonNullable<React.ReactNode>
}

export const FooterLinkList: React.FC<FooterLinkListProps> = ({ children }) => {
  return (
    <>
      <StyledBox>
        <StyledFooterLinkList>
          {children}
        </StyledFooterLinkList>
      </StyledBox>
      <StyledAccordion>
        {children}
      </StyledAccordion>
    </>
  )
}

const StyledInternalLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.theme.palette.custom.footer.text};
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.palette.secondary.main};
  }
`

const StyledExternalLink = styled('a')`
  text-decoration: none;
  color: ${props => props.theme.palette.custom.footer.text};
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.palette.secondary.main};
  }
`

const StyledBox = styled(Box)`
  display: none;

  ${props => props.theme.breakpoints.up('sm')} {
    display: flex;
    justify-content: center;
  }
    
  ${props => props.theme.breakpoints.up('md')} {
    justify-content: flex-end;
  }
`

const StyledFooterLinkList = styled(List)`
  display: inline-block;
  width: fit-content;
`

const StyledListItem = styled(ListItem)`
  display: none;

  ${props => props.theme.breakpoints.up('sm')} {
    display: block;
    padding: ${(props) => props.theme.typography.pxToRem(6)} 0;
  }
`

const StyledLinkTypography = styled(Typography)`
  font-size: ${pxToRem(16)};
  text-decoration: none;
  color: ${props => props.theme.palette.custom.footer.text};
  ${props => props.theme.breakpoints.up('sm')} {
    font-size: ${pxToRem(14)};
  }
  &:hover {
    color: ${props => props.theme.palette.secondary.main};
    cursor: pointer;
    text-decoration: underline;
  }
`


const StyledHeaderTypography = styled(Typography)`
  font-weight: 500;
  margin-bottom: ${(props) => props.theme.typography.pxToRem(4)};
  color: ${props => props.theme.palette.custom.footer.text};
`
